// Admin Basis Pengetahuan: the "Dari Google Drive" section (phase 3). Files
// the admin uploads to the folder Basis Pengetahuan in Google Drive become knowledge
// automatically (every 15 minutes, or "Sinkronkan sekarang").
//   status   plain spacing   Terakhir sinkron, counts per status, Sinkronkan sekarang
//   notice   notice          not ready (no key, no folder, key refused, folder unreadable): link to Sumber Pengetahuan
//   files    rows            table from 1280 (name, folder/category, status, reason, time, Proses ulang), rows below
//   empty    empty state     "Unggah dokumen ke folder <nama> di Google Drive."
import { useState } from "react";
import * as API from "../api";
import * as Sync from "../knowledge-sync-api";
import { formatCount, formatDateTime } from "../lib/format";
import { KNOWLEDGE_CATEGORY_LABEL } from "../lib/labels";
import { Badge, EmptyState, Notice, SectionHead, SkeletonRows, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";

export const SYNC_STATUS_LABEL: Record<Sync.SyncFileStatus, string> = {
  pending: "Menunggu",
  processing: "Diproses",
  ok: "Berhasil",
  failed: "Gagal",
  skipped: "Dilewati",
};

const STATUS_TONE: Record<Sync.SyncFileStatus, "neutral" | "success" | "warning" | "danger" | "info"> = {
  pending: "neutral",
  processing: "info",
  ok: "success",
  failed: "danger",
  skipped: "warning",
};

const COUNT_ORDER: Sync.SyncFileStatus[] = ["ok", "failed", "skipped", "pending", "processing"];

export const NOT_READY_TEXT: Record<Sync.SyncNotReadyReason, { title: string; body: string }> = {
  no_key: { title: "Kunci service account belum disimpan", body: "Simpan kunci service account di Sumber Pengetahuan agar dokumen di folder Basis Pengetahuan menjadi pengetahuan Pluto." },
  no_folder: { title: "Folder Basis Pengetahuan belum dipilih", body: "Pilih folder Basis Pengetahuan di Sumber Pengetahuan." },
  key_rejected: { title: "Kunci service account ditolak Google", body: "Ganti kunci service account di Sumber Pengetahuan dengan kunci JSON yang baru." },
  folder_unreadable: { title: "Folder Basis Pengetahuan tidak dapat dibaca", body: "Pastikan folder masih dibagikan ke email service account (akses Pelihat), lalu periksa di Sumber Pengetahuan." },
  unavailable: { title: "Google Drive belum dapat dihubungi", body: "Coba lagi beberapa saat lagi. Bila tetap gagal, periksa koneksi di Sumber Pengetahuan." },
};

const PAGE_SIZE = 25;

function categoryText(f: Sync.SyncFile): string {
  if (!f.category) return f.path.length > 0 ? f.path.join(" / ") : "Belum ditentukan";
  return KNOWLEDGE_CATEGORY_LABEL[f.category] ?? f.category;
}

export function summaryText(s: Sync.SyncSummary): string {
  if (s.processed === 0 && s.removed === 0) return "Tidak ada berkas baru atau berubah.";
  const parts = [`${formatCount(s.processed)} berkas diproses (${formatCount(s.ok)} berhasil, ${formatCount(s.failed)} gagal, ${formatCount(s.skipped)} dilewati)`];
  if (s.removed > 0) parts.push(`${formatCount(s.removed)} dihapus`);
  if (s.remaining > 0) parts.push(`${formatCount(s.remaining)} masih menunggu dan diproses pada sinkronisasi berikutnya`);
  return `${parts.join(", ")}.`;
}

async function uploadFolderName(): Promise<string | null> {
  try {
    return (await API.getKnowledgeSource()).folder?.name ?? null;
  } catch {
    return null;
  }
}

export function DriveKnowledgeSection() {
  const [page, setPage] = useState(1);
  const status = useAsync(() => Sync.getSyncStatus(), []);
  const files = useAsync(() => Sync.listSyncFiles({ page, pageSize: PAGE_SIZE }), [page]);
  const folder = useAsync(() => uploadFolderName(), []);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<{ tone: "success" | "danger"; title: string; body: string } | null>(null);
  const [redoing, setRedoing] = useState<string | null>(null);

  async function runNow() {
    setRunning(true);
    setOutcome(null);
    try {
      const summary = await Sync.runSyncNow();
      setOutcome({ tone: "success", title: "Sinkronisasi selesai", body: summaryText(summary) });
    } catch (err) {
      setOutcome({ tone: "danger", title: "Sinkronisasi belum berjalan", body: Sync.errorMessage(err) });
    } finally {
      setRunning(false);
      status.reload();
      files.reload();
    }
  }

  async function reprocess(f: Sync.SyncFile) {
    setRedoing(f.driveId);
    setOutcome(null);
    try {
      const updated = await Sync.reprocessSyncFile(f.driveId);
      if (files.data) files.set({ ...files.data, files: files.data.files.map((x) => (x.driveId === updated.driveId ? updated : x)) });
      setOutcome(
        updated.status === "ok"
          ? { tone: "success", title: "Berkas diproses ulang", body: `${updated.title ?? updated.name} sudah menjadi pengetahuan Pluto.` }
          : updated.status === "pending"
            ? { tone: "success", title: "Berkas masuk antrean", body: `${updated.name} diproses pada sinkronisasi berikutnya.` }
            : { tone: "danger", title: "Berkas belum berhasil diproses", body: updated.reason ?? "Coba lagi nanti." },
      );
      status.reload();
    } catch (err) {
      setOutcome({ tone: "danger", title: "Berkas belum diproses ulang", body: Sync.errorMessage(err) });
    } finally {
      setRedoing(null);
    }
  }

  const st = status.data;
  const notReady = st && !st.ready ? NOT_READY_TEXT[st.reason ?? "no_key"] : null;
  const list = files.data?.files ?? [];
  const total = files.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const folderName = folder.data ? `"${folder.data}"` : "unggahan admin";
  const counts = st ? COUNT_ORDER.filter((k) => k !== "processing" || st.counts.processing > 0).map((k) => `${SYNC_STATUS_LABEL[k]} ${formatCount(st.counts[k])}`) : [];

  return (
    <section className="region" aria-labelledby="kb-drive-title">
      <SectionHead
        id="kb-drive-title"
        title="Dari Google Drive"
        lead={`Dokumen di folder ${folderName} di Google Drive otomatis menjadi pengetahuan Pluto dan muncul di Regulasi. Disinkronkan setiap ${st?.intervalMinutes ?? 15} menit.`}
        action={
          <button type="button" className="btn btn-secondary" onClick={runNow} aria-busy={running || undefined} disabled={running || !!notReady}>
            <Glyph name="refresh" />
            <span>{running ? "Menyinkronkan" : "Sinkronkan sekarang"}</span>
          </button>
        }
      />

      {st ? (
        <p className="cell-meta kb-drive-status" data-testid="kb-drive-status">
          {st.running ? "Sinkronisasi sedang berjalan. " : ""}
          {st.lastSyncAt ? `Terakhir sinkron ${formatDateTime(st.lastSyncAt)}.` : "Belum pernah disinkronkan."} {counts.join(", ")}
        </p>
      ) : status.error ? (
        <Notice tone="danger" title="Status sinkronisasi belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={status.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(status.error)}</p>
        </Notice>
      ) : null}

      {outcome ? (
        <Notice tone={outcome.tone} title={outcome.title}>
          <p>{outcome.body}</p>
        </Notice>
      ) : null}

      {notReady ? (
        <Notice
          tone="warning"
          title={notReady.title}
          action={
            <a className="btn btn-secondary" href="/admin/sumber-pengetahuan">
              Buka Sumber Pengetahuan
            </a>
          }
        >
          <p>{notReady.body}</p>
        </Notice>
      ) : null}

      {files.loading && !files.data ? (
        <SkeletonRows count={3} label="Memuat berkas dari Google Drive" />
      ) : files.error ? (
        <Notice tone="danger" title="Daftar berkas belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={files.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(files.error)}</p>
        </Notice>
      ) : total === 0 ? (
        notReady ? null : (
          <EmptyState icon="folder" title="Belum ada berkas dari Google Drive" body={`Unggah dokumen ke folder ${folderName} di Google Drive. Subfolder boleh dipakai; namanya menjadi kategori.`} />
        )
      ) : (
        <>
          <div className="records-table" data-from="1280">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Berkas</th>
                  <th scope="col">Folder atau kategori</th>
                  <th scope="col">Status</th>
                  <th scope="col">Alasan</th>
                  <th scope="col">Waktu</th>
                  <th scope="col" className="action-col">
                    Tindakan
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((f) => (
                  <tr key={f.driveId}>
                    <td className="kb-title-cell">
                      <span className="cell-title">{f.title ?? f.name}</span>
                      {f.title && f.title !== f.name ? <span className="cell-meta">{f.name}</span> : null}
                    </td>
                    <td className="nowrap">{categoryText(f)}</td>
                    <td className="nowrap">
                      <Badge tone={STATUS_TONE[f.status]}>{SYNC_STATUS_LABEL[f.status]}</Badge>
                    </td>
                    <td>{f.reason ?? ""}</td>
                    <td className="num nowrap">{formatDateTime(f.processedAt ?? f.updatedAt)}</td>
                    <td className="action-col">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => reprocess(f)}
                        aria-busy={redoing === f.driveId || undefined}
                        disabled={redoing !== null || f.status === "processing"}
                        aria-label={`Proses ulang ${f.name}`}
                      >
                        Proses ulang
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="rows records-list" data-from="1280">
            {list.map((f) => (
              <li key={f.driveId} className="row">
                <span className="row-main">
                  <span className="row-title">{f.title ?? f.name}</span>
                  <span className="row-meta">
                    {categoryText(f)}, {formatDateTime(f.processedAt ?? f.updatedAt)}
                  </span>
                  {f.reason ? <span className="row-meta">{f.reason}</span> : null}
                </span>
                <span className="row-side">
                  <Badge tone={STATUS_TONE[f.status]}>{SYNC_STATUS_LABEL[f.status]}</Badge>
                  <button type="button" className="btn btn-secondary" onClick={() => reprocess(f)} disabled={redoing !== null || f.status === "processing"}>
                    Proses ulang
                  </button>
                </span>
              </li>
            ))}
          </ul>
          {pageCount > 1 ? (
            <div className="db-pager" role="group" aria-label="Halaman berkas Google Drive">
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1}>
                Sebelumnya
              </button>
              <span className="cell-meta">
                Halaman {page} dari {pageCount}
              </span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(p + 1, pageCount))} disabled={page >= pageCount}>
                Berikutnya
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
